import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Tag, ShieldCheck, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { ImageWithPlaceholder } from '../ImageWithPlaceholder/ImageWithPlaceholder';
import { StarRating } from '../StarRating/StarRating';
import { getImageUrl } from '../../lib/supabase';
import { formatPrice, whatsappOrderLink } from '../../lib/utils';

/**
 * Product listing card — horizontal layout with image on the left
 * and details on the right.
 *
 * Desktop : image left (fixed width) + info right
 * Mobile  : stacks vertically (image top, info bottom)
 */
export function ProductCard({ product, index = 0, className = '' }) {
  const imageUrl = getImageUrl(product.image_path);
  const categoryName = product.categories?.name;
  const orderLink = whatsappOrderLink(product);

  return (
    <Link to={`/menu/${product.slug}`} className="block no-underline">
      <motion.article
        layout
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.25) }}
        className={clsx(
          'group flex flex-col sm:flex-row gap-0 sm:gap-4 bg-brand-white rounded-xl',
          'border border-ui-border hover:shadow-lg transition-all duration-200 overflow-hidden',
          className
        )}
      >
        {/* Image */}
        <div className="relative sm:w-48 lg:w-56 flex-shrink-0 overflow-hidden">
          <ImageWithPlaceholder
            src={imageUrl}
            alt={product.title}
            aspectRatio="aspect-[16/10] sm:aspect-auto"
            className="w-full h-full sm:h-36 lg:h-40 group-hover:scale-105 transition-transform duration-300"
          />

          {/* Badges on image */}
          {product.is_featured && (
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-brand-red text-brand-white text-[10px] font-bold rounded-md shadow">
              Featured
            </span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 flex flex-col p-3 sm:p-0 sm:py-3 sm:pr-4">
          {/* Top: title + price */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-bold text-brand-dark leading-snug group-hover:text-brand-red transition-colors line-clamp-1">
                {index > 0 && (
                  <span className="text-brand-gray/60 mr-1">{index}.</span>
                )}
                {product.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <StarRating rating={5} size={12} />
                {categoryName && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-brand-gray/30" />
                    <span className="flex items-center gap-1 text-[11px] text-brand-gray">
                      <Tag size={10} />
                      {categoryName}
                    </span>
                  </>
                )}
              </div>
            </div>
            <span className="text-sm sm:text-base font-extrabold text-brand-dark flex-shrink-0">
              {formatPrice(product.price)}
            </span>
          </div>

          {/* Description */}
          {product.short_description && (
            <p className="mt-1.5 text-xs sm:text-sm text-brand-gray leading-relaxed line-clamp-2">
              {product.short_description}
            </p>
          )}

          {/* Bottom row */}
          <div className="mt-auto pt-2 sm:pt-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-success font-medium">
              <ShieldCheck size={12} />
              <span>Verified</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={orderLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-success text-brand-white text-[11px] sm:text-xs font-bold rounded-lg hover:bg-green-700 transition-colors"
              >
                <MessageCircle size={12} />
                <span className="hidden sm:inline">Order</span>
              </a>
              <span className="text-brand-gray group-hover:text-brand-red transition-colors">
                <ChevronRight size={16} />
              </span>
            </div>
          </div>
        </div>
      </motion.article>
    </Link>
  );
}
